import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  ArrowLeft,
  Languages,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Settings,
  ShieldCheck,
  Sun,
  X,
} from "lucide-react";
import {
  type ComponentType,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.js";
import { useLogout, useSession } from "../auth/session.js";
import { useOnline } from "../pwa.js";
import { type ThemePreference, useTheme } from "../theme.js";
import { Avatar } from "./avatar.js";
import { BillingBanner } from "./billing-banner.js";
import { ShellSlotsContext } from "./shell-slots.js";

export type Icon = ComponentType<{ className?: string; strokeWidth?: number }>;
export interface NavItem {
  to: string;
  key: string;
  icon: Icon;
  /** Active only on this exact path (a section's index page). */
  exact?: boolean;
}

/**
 * The frame every signed-in surface shares, laid out like Google's: a top bar with the search and
 * the account, an optional app rail, a side column (the page's own content, or fixed navigation),
 * and the page on a white card. On phones the top bar becomes a search pill, the side column a
 * drawer, and the apps move to a bottom bar.
 */
export function ShellFrame({
  title,
  brandSuffix,
  homeTo,
  rail,
  sideNav,
  drawerFooter,
  banner,
  inConsole = false,
  children,
}: {
  /** Shown in the top bar when the page puts no search there. */
  title: string;
  /** A word after the logo, e.g. "Admin" in the console. */
  brandSuffix?: string;
  homeTo: string;
  /** Apps on the left rail (wide screens) and the bottom bar (phones). */
  rail?: ReadonlyArray<NavItem>;
  /** Fixed navigation above the page's own side column. */
  sideNav?: ReactNode;
  /** Extra links at the bottom of the phone drawer. */
  drawerFooter?: ReactNode;
  /** A quiet line at the top of the content card. */
  banner?: ReactNode;
  inConsole?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const session = useSession();
  const online = useOnline();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [search, setSearch] = useState<HTMLElement | null>(null);
  const [sidebar, setSidebar] = useState<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const slots = useMemo(() => ({ search, sidebar, closeDrawer }), [search, sidebar, closeDrawer]);

  // A new page closes the drawer.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on navigation on purpose
  useEffect(() => setDrawerOpen(false), [pathname]);

  const brand = (
    <>
      <img src="/icon.svg" alt="" className="size-8" />
      <span className="text-[22px] tracking-tight text-ink-2">
        {t("app.name")}
        {brandSuffix ? <span className="ml-1.5 text-ink-3">{brandSuffix}</span> : null}
      </span>
    </>
  );

  return (
    <ShellSlotsContext.Provider value={slots}>
      <div className="flex h-full flex-col bg-bg">
        <header className="shrink-0 px-2 pt-2 pb-1 md:px-3 md:py-2">
          <div className="flex h-12 items-center gap-1 rounded-full bg-surface-2 pr-1 md:gap-2 md:rounded-none md:bg-transparent md:pr-0">
            <button
              type="button"
              aria-label={t("shell.menu")}
              className="flex size-12 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
              onClick={() => {
                if (window.matchMedia("(min-width: 768px)").matches) setCollapsed((v) => !v);
                else setDrawerOpen(true);
              }}
            >
              <Menu className="size-5" />
            </button>
            <Link
              to={homeTo}
              className="hidden w-[196px] shrink-0 items-center gap-2 md:flex"
              aria-label={t("app.name")}
            >
              {brand}
            </Link>
            <div className="flex h-12 min-w-0 flex-1 items-center md:max-w-[720px] md:rounded-full md:bg-surface-2 md:focus-within:bg-surface md:focus-within:shadow-1">
              <div
                ref={setSearch}
                className="peer flex h-full min-w-0 flex-1 items-center empty:hidden"
              />
              <span className="hidden truncate px-2 text-base text-ink-2 peer-empty:block md:px-5">
                {title}
              </span>
            </div>
            <div className="hidden flex-1 md:block" />
            <Link
              to="/settings/mail"
              aria-label={t("nav.settings")}
              className="hidden size-12 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5 md:flex"
            >
              <Settings className="size-5" />
            </Link>
            <AccountMenu inConsole={inConsole} />
          </div>
        </header>

        <div className="flex min-h-0 flex-1">
          {rail ? (
            <nav
              aria-label={t("shell.apps")}
              className="hidden w-[72px] shrink-0 flex-col items-center gap-3 pt-2 md:flex"
            >
              {rail.map((app) => (
                <RailLink key={app.to} to={app.to} label={t(app.key)} icon={app.icon} />
              ))}
            </nav>
          ) : null}

          {drawerOpen ? (
            <button
              type="button"
              aria-label={t("shell.closeMenu")}
              className="fixed inset-0 z-30 bg-black/40 md:hidden"
              onClick={closeDrawer}
            />
          ) : null}
          <aside
            className={`${sideNav ? "" : "shell-sidebar"} fixed inset-y-0 left-0 z-40 flex w-[280px] flex-col overflow-y-auto bg-surface pb-4 transition-transform md:static md:z-auto md:w-[256px] md:shrink-0 md:translate-x-0 md:bg-transparent md:pb-3 md:shadow-none ${drawerOpen ? "translate-x-0 shadow-2" : "-translate-x-full"} ${collapsed ? "md:hidden" : ""}`}
            onKeyDown={(event) => {
              if (event.key === "Escape") closeDrawer();
            }}
          >
            <div className="flex items-center gap-2 px-4 pt-4 pb-2 md:hidden">
              <Link to={homeTo} className="flex flex-1 items-center gap-2">
                {brand}
              </Link>
              <button
                type="button"
                aria-label={t("shell.closeMenu")}
                className="flex size-10 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
                onClick={closeDrawer}
              >
                <X className="size-5" />
              </button>
            </div>
            {sideNav}
            <div ref={setSidebar} className="shell-sidebar-slot flex flex-col" />
            {drawerFooter ? (
              <div className="mt-4 border-t border-rule px-3 pt-3 md:hidden">{drawerFooter}</div>
            ) : null}
          </aside>

          <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-surface md:mr-3 md:mb-3 md:rounded-2xl">
            {online ? null : (
              <div
                className="shrink-0 bg-surface-2 px-4 py-1.5 text-center text-xs text-ink-2"
                role="status"
              >
                {t("app.offline")}
              </div>
            )}
            {session.data && !session.data.user.emailVerified ? (
              <UnverifiedBanner email={session.data.user.email} />
            ) : null}
            <BillingBanner />
            {banner}
            <div className="min-h-0 flex-1 overflow-auto">{children}</div>
          </main>
        </div>

        {rail ? (
          <nav
            aria-label={t("shell.apps")}
            className="flex shrink-0 border-t border-rule bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
          >
            {rail.map((app) => (
              <Link
                key={app.to}
                to={app.to}
                className="flex h-16 flex-1 flex-col items-center justify-center gap-1 text-[11px] text-ink-2"
                activeProps={{ className: "text-accent font-medium" }}
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`flex h-8 w-14 items-center justify-center rounded-full ${isActive ? "bg-accent-soft" : ""}`}
                    >
                      <app.icon className="size-5" strokeWidth={isActive ? 2.2 : 1.8} />
                    </span>
                    {t(app.key)}
                  </>
                )}
              </Link>
            ))}
          </nav>
        ) : null}
      </div>
    </ShellSlotsContext.Provider>
  );
}

function RailLink({ to, label, icon: IconComponent }: { to: string; label: string; icon: Icon }) {
  return (
    <Link to={to} className="flex flex-col items-center gap-1 text-xs text-ink-2">
      {({ isActive }) => (
        <>
          <span
            className={`flex h-8 w-14 items-center justify-center rounded-full ${isActive ? "bg-accent-soft text-accent" : "hover:bg-ink/5"}`}
          >
            <IconComponent className="size-5" strokeWidth={isActive ? 2.2 : 1.8} />
          </span>
          <span className={isActive ? "font-medium text-ink" : ""}>{label}</span>
        </>
      )}
    </Link>
  );
}

/** A left-column navigation entry, styled like Gmail's folders. */
export function SideNavLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const { t } = useTranslation();
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      activeOptions={{ exact: Boolean(item.exact) }}
      className="flex h-10 items-center gap-4 rounded-r-full pr-3 pl-6 text-sm text-ink-2 hover:bg-ink/5"
      activeProps={{ className: "bg-accent-soft font-medium text-accent hover:bg-accent-soft" }}
    >
      {({ isActive }) => (
        <>
          <item.icon className="size-[18px] shrink-0" strokeWidth={isActive ? 2.2 : 1.8} />
          <span className="truncate">{t(item.key)}</span>
        </>
      )}
    </Link>
  );
}

const THEMES: ReadonlyArray<{ value: ThemePreference; key: string; icon: Icon }> = [
  { value: "light", key: "shell.theme.light", icon: Sun },
  { value: "dark", key: "shell.theme.dark", icon: Moon },
  { value: "system", key: "shell.theme.system", icon: Monitor },
];

/** Avatar button with the account, appearance, language, console and sign-out. */
function AccountMenu({ inConsole }: { inConsole: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const session = useSession();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useTheme();
  const user = session.data?.user;
  const isAdmin = user?.role === "owner" || user?.role === "admin";
  const nextLocale = i18n.language === "tr" ? "en" : "tr";

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const item = "flex h-11 items-center gap-3 rounded-xl px-3 text-sm hover:bg-surface-2";
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        aria-label={t("shell.account")}
        aria-expanded={open}
        className="flex size-12 items-center justify-center rounded-full hover:bg-ink/5"
        onClick={() => setOpen((v) => !v)}
      >
        <Avatar name={user?.displayName || user?.email || "?"} size={32} />
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-label={t("common.close")}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute top-full right-0 z-50 mt-1 w-[320px] max-w-[calc(100vw-16px)] rounded-3xl bg-surface-2 p-3 shadow-2">
            <div className="flex flex-col items-center gap-2 px-2 pt-2 pb-3 text-center">
              <Avatar name={user?.displayName || user?.email || "?"} size={64} />
              <div className="text-base font-medium">{user?.displayName}</div>
              <div className="max-w-full truncate text-sm text-ink-3">{user?.email}</div>
              <div className="text-xs text-ink-3">{session.data?.tenant.name}</div>
            </div>
            <div className="flex flex-col gap-0.5 rounded-2xl bg-surface p-1">
              {inConsole ? (
                <Link to="/mail" onClick={() => setOpen(false)} className={item}>
                  <ArrowLeft className="size-5 text-ink-2" />
                  {t("admin.backToMail")}
                </Link>
              ) : isAdmin ? (
                <Link to="/admin" onClick={() => setOpen(false)} className={item}>
                  <ShieldCheck className="size-5 text-ink-2" />
                  {t("admin.console")}
                </Link>
              ) : null}
              <Link to="/settings/mail" onClick={() => setOpen(false)} className={item}>
                <Settings className="size-5 text-ink-2" />
                {t("nav.settings")}
              </Link>
              <button
                type="button"
                className={`${item} text-left`}
                onClick={() => void i18n.changeLanguage(nextLocale)}
              >
                <Languages className="size-5 text-ink-2" />
                <span className="flex-1">{t("shell.language")}</span>
                <span className="text-xs text-ink-3">{i18n.language.toUpperCase()}</span>
              </button>
              <fieldset className="flex items-center gap-1 px-2 py-2">
                <legend className="sr-only">{t("shell.theme.title")}</legend>
                {THEMES.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={theme === option.value}
                    className={`flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full border text-xs ${theme === option.value ? "border-accent bg-accent-soft text-accent" : "border-rule text-ink-2 hover:bg-surface-2"}`}
                    onClick={() => setTheme(option.value)}
                  >
                    <option.icon className="size-4" />
                    {t(option.key)}
                  </button>
                ))}
              </fieldset>
            </div>
            <button
              type="button"
              className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-full text-sm text-ink-2 hover:bg-surface"
              disabled={logout.isPending}
              onClick={() =>
                logout.mutate(undefined, { onSettled: () => void navigate({ to: "/login" }) })
              }
            >
              <LogOut className="size-4" />
              {t("auth.signOut")}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

const DISMISS_KEY = "websede.unverified-dismissed";

/** One quiet line under the top bar; dismissing it hides it for the rest of the session. */
function UnverifiedBanner({ email }: { email: string }) {
  const { t } = useTranslation();
  const resend = useMutation({ mutationFn: () => api.auth.email.resend() });
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === email;
    } catch {
      return false;
    }
  });
  if (dismissed) return null;
  return (
    <div className="flex shrink-0 items-center gap-3 border-b border-rule bg-warn-soft px-4 py-1.5 text-xs text-warn">
      <span className="min-w-0 flex-1 truncate">{t("banner.unverified", { email })}</span>
      <button
        type="button"
        className="shrink-0 font-medium underline-offset-2 hover:underline disabled:opacity-60"
        disabled={resend.isPending || resend.isSuccess}
        onClick={() => resend.mutate()}
      >
        {resend.isSuccess ? t("banner.resent") : t("banner.resend")}
      </button>
      <button
        type="button"
        aria-label={t("common.close")}
        className="shrink-0 rounded-full p-1 hover:bg-ink/5"
        onClick={() => {
          try {
            sessionStorage.setItem(DISMISS_KEY, email);
          } catch {
            // Storage blocked: it stays hidden until the next load.
          }
          setDismissed(true);
        }}
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
